<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Stepper from 'primevue/stepper';
import Step from 'primevue/step';
import StepList from 'primevue/steplist';
import Divider from 'primevue/divider';

import { useCurrentSegment } from '@/composables/useCurrentSegment';
import { useNavigationHelpers } from '@/composables/useNavigationHelpers';
import type { ViewGroup } from '@/utils/types';

const route = useRoute();
const router = useRouter();

const { navigableSegmentGroups } = useCurrentSegment();
const { getStepperNavigation } = useNavigationHelpers();

const currentGroupIndex = computed(() => {
  return (
    Number(
      navigableSegmentGroups.value?.findIndex((group) => group.id === Number(route.params.groupId))
    ) + 1
  );
});

const handleStepperUpdate = (group: ViewGroup) => {
  const { shouldNavigate, route: navigationRoute } = getStepperNavigation(
    group,
    route.params
  );

  if (shouldNavigate && navigationRoute) {
    router.push(navigationRoute);
  }
};
</script>

<template>
  <div class="max-w-5xl overflow-x-auto">
    <Stepper v-if="navigableSegmentGroups.length > 1" :value="currentGroupIndex">
      <StepList>
        <Step
          :key="group.id"
          v-for="(group, index) in navigableSegmentGroups"
          :value="index + 1"
          @click="handleStepperUpdate(group)"
          asChild
        >
          <div
            class="flex items-center"
            :class="{
              'flex-1': index !== navigableSegmentGroups.length - 1,
            }"
          >
            <button
              class="flex h-7 w-7 min-w-7 items-center justify-center rounded-full border-2 text-sm"
              :class="{
                'border-brand-500 bg-brand-500 text-white': index + 1 === currentGroupIndex,
                'border-[#e2e8f0] text-[#64748b]': index + 1 !== currentGroupIndex,
              }"
              v-tooltip.top="group.name"
              @click="handleStepperUpdate(group)"
            >
              {{ index + 1 }}
            </button>
            <Divider v-if="index !== navigableSegmentGroups.length - 1" />
          </div>
        </Step>
      </StepList>
    </Stepper>
  </div>
</template>
